import { getAllSupportPersonnelRequest } from '../utils'
import type { SupportPerson } from '@gat-ship/types'
import { useSupportSignalREvents } from '../utils/support-signalR-events'
import { defineStore } from 'pinia'
import { shallowRef } from 'vue'

export function useSupportPersonnelDataSource() {
  /**
   * The support personnel data source.
   */
  const supportPersonnel = shallowRef<SupportPerson[]>([])

  /**
   * Sets the support personnel data source.
   * @param personnel - The support personnel to set.
   */
  function setSupportPersonnel(personnel: SupportPerson[]) {
    supportPersonnel.value = personnel
  }

  /**
   * Fetches support personnel from the API.
   * @param options - Options for the fetch operation.
   * @param options.forceFetch - Whether to force a fetch of the data.
   * @param options.subscribeToEvents - Whether to subscribe to support personnel events. @default true
   */
  async function fetchSupportPersonnel(options?: {
    forceFetch?: boolean
    subscribeToEvents?: boolean
  }) {
    if (supportPersonnel.value.length === 0 || options?.forceFetch) {
      const result = await getAllSupportPersonnelRequest()
      if (result) {
        setSupportPersonnel(result.personnel)
      }
    }
    if (options?.subscribeToEvents ?? true) {
      subscribeToEvents()
    }
  }

  const supportSignalREvents = useSupportSignalREvents(/** No events yet. */)

  function unsubscribeFromEvents() {
    supportSignalREvents.unsubscribeFromSupportSessionEvents()
  }

  function subscribeToEvents() {
    supportSignalREvents.subscribeToSupportSessionEvents()
    return () => {
      unsubscribeFromEvents()
    }
  }

  return {
    supportPersonnel,
    fetchSupportPersonnel,
    clearData: () => {
      setSupportPersonnel([])
    },
    subscribeToEvents,
    unsubscribeFromEvents
  }
}

export const useSupportPersonnelStore = defineStore('support-personnel', () => {
  const supportPersonnelDataSource = useSupportPersonnelDataSource()

  return {
    ...supportPersonnelDataSource
  }
})
